import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RefreshIcon from '@mui/icons-material/Refresh'
import AddIcon from '@mui/icons-material/Add'
// import { HubSpotLogo } from 'src/assets/integrations/HubSpotLogo'
import { useNavigate } from 'react-router-dom'
import { useGetMeQuery } from 'src/services/user-service'
import { config, planNames } from 'src/config.js'
import { toast } from 'react-hot-toast'
import { useUpdateApiKeyMutation, useGetApiKeyMutation } from 'src/services/integration-service'
import { useSelector } from 'react-redux'
import axios from 'axios'
import { Starwhite } from 'src/icons/starwhite'
import { useGetHubspotIntegrationQuery } from 'src/services/hubspot-service'
import PremiumFeatureDialog from 'src/utils/premiumFeatureDialog'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import { useGetCurrentPlanQuery } from '@services/billing-service'
import useUserVerification from '@hooks/useUserVerification'
import { useAuth } from '@auth/auth'

const SuccessAiApiGenerateDialog = ({ open, setOpenSuccessaiApiGenerateDialog, handleCloseSuccessaiApiDialog, updateApiKey, isLoadingUpdateApiKey, workspace }) => {
  const handleGenerateKey = async () => {
    try {
      const toastId = toast.loading('Generating new API key', { duration: Infinity })
      const { message } = await updateApiKey({ workspace }).unwrap()
      toast.success(message, { id: toastId, duration: 2000 })
      setOpenSuccessaiApiGenerateDialog(false)
    } catch (error) {
      toast.error('Failed to generate API key. Please try again later.')
    }
  }

  return (
    <Dialog open={open} onClose={handleCloseSuccessaiApiDialog}>
      <DialogTitle sx={{ fontSize: '22px', fontWeight: '700' }}>Are you sure?</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ letterSpacing: '1px', fontWeight: 400, fontSize: '15px' }}>
          This will generate a new API key and invalidate the old one.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ margin: 2 }}>
        <Button onClick={handleCloseSuccessaiApiDialog} color='primary' variant='outlined'>
          Cancel
        </Button>
        <Button
          disabled={isLoadingUpdateApiKey}
          onClick={handleGenerateKey}
          color='primary'
          variant='contained'
        >
          Generate New API Key
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const IntegrationsApiPage = () => {
  const navigate = useNavigate()
  const { data: user } = useGetMeQuery()
  // const { data } = useGetMeAndWorkspaceQuery()
  const workspace = useSelector((state) => state.workspace)
  const [updateApiKey, { isLoading: isLoadingUpdateApiKey }] = useUpdateApiKeyMutation()
  const [getApiKey] = useGetApiKeyMutation()
  const [sucessaiApiKey, setSuccessaiApiKey] = useState('')
  const [openSuccessaiApiGenerateDialog, setOpenSuccessaiApiGenerateDialog] = useState(false)
  const [isZapierEnabled, setIsZapierEnabled] = useState(false)
  const [zapierKey, setZapierKey] = useState('')
  const { data: HubSpotIntegrationData } = useGetHubspotIntegrationQuery()
  const [openModal, setOpenModal] = useState(false)
  const email = user?.email
  const { canEdit } = useAuth()

  const { isFreeUser, isEarlyBirdAccessUser, isAppSumoUser, isWhiteLabelUser, isExcludedIntegrationFeature, hasAnyLeadsGrowthPlan, hasAnyLeadsInfinityPlan, hasAnyOutreachGrowthPlan, hasAnyOutreachInfinityPlan } = useUserVerification()
  const currentPlan = useGetCurrentPlanQuery()
  const planId = currentPlan?.currentData?.subscription?.sendingWarmup?.planId || currentPlan?.currentData?.subscription?.leads?.planId
  // const planName = isEarlyBirdAccessUser ? 'earlyBird' : (planNames[planId] ?? 'free')

  const PLAN_MONTHLY_GROWTH = 'Monthly Growth'
  const PLAN_YEARLY_GROWTH = 'Yearly Growth'
  const PLAN_MONTHLY_INFINITY = 'Monthly Infinity'
  const PLAN_YEARLY_INFINITY = 'Yearly Infinity'
  const checkPlanForGrowthAndInfinty = () => {
    const planName = planNames[planId]
    return [PLAN_MONTHLY_GROWTH, PLAN_YEARLY_GROWTH, PLAN_MONTHLY_INFINITY, PLAN_YEARLY_INFINITY].includes(planName)
  }

  const handleClickOpenSuccessaiApiDialog = () => {
    if (isFreeUser) {
      setOpenModal(true)
      return
    }
    setOpenSuccessaiApiGenerateDialog(true)
  }

  const handleCloseSuccessaiApiDialog = () => {
    setOpenSuccessaiApiGenerateDialog(false)
  }

  const getSuccessaiApiKeyForCopy = async () => { // SuccessAi api
    const { apiKey } = await getApiKey({ workspace: workspace?._id }).unwrap()
    setSuccessaiApiKey(apiKey)
  }

  const handleSuccessaiApiCopy = async () => {
    try {
      await navigator.clipboard.writeText(sucessaiApiKey)
      toast.success('Copied to Clipboard')
    } catch (err) {
      toast.error('Unable to copy to clipboard')
    }
  }

  useEffect(() => {
    getSuccessaiApiKeyForCopy()
  }, [isLoadingUpdateApiKey])

  const addZapierApiKey = async (checked) => {
    if (email) {
      try {
        const result = await axios.post(`${config.API_BASE_URL}/integrations/store-zapier-key`, {
          email,
          checked
        })

        if (result !== undefined) {
          setZapierKey(result?.data?.zapierApiKey)
          setIsZapierEnabled(checked)
        } else {
          throw new Error('No API key returned from server.')
        }
      } catch (error) {
        toast.error('Failed to add Zapier key. Please try again.', { duration: 2000 })
        throw error
      }
    }
  }

  const handleZapierToggle = async (event) => {
    try {
      const checked = event.target.checked
      await addZapierApiKey(checked)
    } catch (error) {
      toast.error('Failed to get Zapier key. Please try again.', { duration: 2000 })
      throw error
    }
  }

  const handleCopyZapierKey = async () => {
    try {
      await navigator.clipboard.writeText(zapierKey)
      toast.success('Copied to Clipboard')
    } catch (err) {
      toast.error('Unable to copy to clipboard')
    }
  }

  useEffect(() => {
    if (email) {
      const getUserIntegration = async () => {
        try {
          const result = await axios.get(`${config.API_BASE_URL}/integrations/get-user-integrations/${email}`)
          if (result !== undefined) {
            setIsZapierEnabled(result?.data?.[0]?.zapierVerifiedFlag === 'Y')
            setZapierKey(result?.data?.[0]?.zapierApiKey)
          } else {
            throw new Error('No Zapier API key returned from server.')
          }
        } catch (error) {
          toast.error('Failed to get Zapier key. Please try again.', { duration: 2000 })
          throw error
        }
      }
      getUserIntegration()
    }
  }, [email])

  const handleChromeExtensionDownload = () => {
    window.open('https://chromewebstore.google.com/detail/successai-email-finder/ohocbgogaaienfhempmeaaedaphghlom', '_blank')
  }

  const handleNavigate = () => {
    navigate('/integrations/hubspot-settings')
  }

  const handleExplorePlans = () => {
    navigate('/settingsUpdated/upgrade')
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleWebhookNavigate = () => {
    navigate('/settingsUpdated/integrations/webhooks')
  }

  const isZapierAllowed = () => {
    return !isFreeUser
  }

  const isSuccessAIApiAllowed = () => {
    // Early Bird and AppSumo users have access to all integrations
    if (isEarlyBirdAccessUser || isAppSumoUser) {
      return true
    }

    // For new users, check if they have Growth or Infinity plans
    const hasGrowthOrInfinityPlan = hasAnyLeadsGrowthPlan || hasAnyLeadsInfinityPlan ||
      hasAnyOutreachGrowthPlan || hasAnyOutreachInfinityPlan

    // For old users, use the existing function to check plan names
    const isOldUserWithGrowthOrInfinity = checkPlanForGrowthAndInfinty()

    return hasGrowthOrInfinityPlan || isOldUserWithGrowthOrInfinity
  }

  const isHubSpotIntegrationAllowed = () => {
    // Early Bird and AppSumo users have access to all integrations
    if (isEarlyBirdAccessUser || isAppSumoUser) {
      return true
    }

    // For new users, check if they have Growth or Infinity plans
    const hasGrowthOrInfinityPlan = hasAnyLeadsGrowthPlan || hasAnyLeadsInfinityPlan ||
      hasAnyOutreachGrowthPlan || hasAnyOutreachInfinityPlan

    // For old users, use the existing function to check plan names
    const isOldUserWithGrowthOrInfinity = checkPlanForGrowthAndInfinty()

    return hasGrowthOrInfinityPlan || isOldUserWithGrowthOrInfinity
  }

  const isWebhookAllowed = () => {
    // Early Bird and AppSumo users have access to all integrations
    if (isEarlyBirdAccessUser || isAppSumoUser) {
      return true
    }

    // For new users, check if they have Growth or Infinity plans
    const hasGrowthOrInfinityPlan = hasAnyLeadsGrowthPlan || hasAnyLeadsInfinityPlan ||
      hasAnyOutreachGrowthPlan || hasAnyOutreachInfinityPlan

    // For old users, use the existing function to check plan names
    const isOldUserWithGrowthOrInfinity = checkPlanForGrowthAndInfinty()

    return hasGrowthOrInfinityPlan || isOldUserWithGrowthOrInfinity
  }

  return (
    <Box
      sx={{
        mt: '24px',
        width: '100%',
        maxWidth: '100%',
        px: { xs: 1, sm: 2 },
        boxSizing: 'border-box'
      }}
    >
      {/* Grid Layout for Cards */}
      <Grid
        container
        spacing={{ xs: 2, sm: 3, md: 2, lg: 3, xl: 3, xxl: 2 }}
        sx={{ justifyContent: 'flex-start' }} // Align cards to the left
      >
        {/* Success.ai API Card */}
        <Grid item xs={12} sm={6} md={4} lg={4} xl={3} xxl={2}>
          <Card
            variant='outlined'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              border: '1px solid #D7D9E6',
              borderRadius: '8px',
              minHeight: '160px',
              maxHeight: 'auto',
              height: '100%',
              maxWidth: { xs: '100%', md: '320px' },
              minWidth: '200px',
              boxSizing: 'border-box'
            }}
          >
            <CardContent
              sx={{
                p: '0px !important',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                    fontWeight: 600
                  }}
                >
                  Success.ai API
                </Typography>
                <Typography
                  color='text.secondary'
                  sx={{
                    mt: '4px',
                    fontSize: { xs: '11px', sm: '12px', md: '13px' },
                    fontWeight: 400
                  }}
                >
                  Leverage the Success.ai API to harness the full power of our tool and seamlessly connect it to your services.
                </Typography>
              </Box>
              <Box sx={{ mt: '4px', width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}
                >
                  {((isWhiteLabelUser && !isExcludedIntegrationFeature) || isSuccessAIApiAllowed())
                    ? (
                      <Button
                        sx={{
                          height: '34px',
                          minWidth: '120px',
                          borderRadius: '6px',
                          gap: '4px',
                          flexShrink: 0,
                          fontSize: { xs: '10px', sm: '11px', md: '12px' }
                        }}
                        variant='contained'
                        onClick={handleClickOpenSuccessaiApiDialog}
                        disabled={!canEdit}
                      >
                        {sucessaiApiKey
                          ? (
                            <RefreshIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                            )
                          : (
                            <AddIcon sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                            )}
                        <Typography
                          sx={{
                            fontSize: { xs: '10px', sm: '11px', md: '12px' },
                            fontWeight: 600,
                            color: '#fff'
                          }}
                        >
                          {sucessaiApiKey ? 'Regenerate' : 'Generate'}
                        </Typography>
                      </Button>
                      )
                    : (
                      <Button
                        sx={{
                          height: '34px',
                          minWidth: '120px',
                          borderRadius: '6px',
                          gap: '4px',
                          flexShrink: 0,
                          fontSize: { xs: '10px', sm: '11px', md: '12px' }
                        }}
                        variant='contained'
                        onClick={handleExplorePlans}
                      >
                        <Starwhite sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        <Typography
                          sx={{
                            fontSize: { xs: '10px', sm: '11px', md: '12px' },
                            fontWeight: 600,
                            color: '#fff'
                          }}
                        >
                          Explore Plans
                        </Typography>
                      </Button>
                      )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {sucessaiApiKey && ((isWhiteLabelUser && !isExcludedIntegrationFeature) || isSuccessAIApiAllowed()) && (
                      <Tooltip title='Copy API Key' placement='top' arrow>
                        <IconButton
                          sx={{
                            p: '4px',
                            bgcolor: '#F2F4F6',
                            border: '1px solid #E4E4E5',
                            borderRadius: '6px'
                          }}
                          disabled={!sucessaiApiKey || isFreeUser || !canEdit}
                          onClick={handleSuccessaiApiCopy}
                        >
                          <ContentCopyIcon
                            fontSize='small'
                            sx={{ color: 'primary.main' }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title='View Documentation' placement='top' arrow>
                      <IconButton
                        component={Link}
                        href={`${config.API_BASE_URL}/docs`}
                        target='_blank'
                        rel='noreferrer'
                        sx={{ p: '4px' }}
                      >
                        <ArticleOutlinedIcon
                          sx={{ fontSize: { xs: '16px', md: '20px' }, color: 'black' }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Zapier Card */}
        <Grid item xs={12} sm={6} md={4} lg={4} xl={3} xxl={2}>
          <Card
            variant='outlined'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              border: '1px solid #D7D9E6',
              borderRadius: '8px',
              minHeight: '160px',
              maxHeight: 'auto',
              height: '100%',
              maxWidth: { xs: '100%', md: '320px' },
              minWidth: '200px',
              boxSizing: 'border-box'
            }}
          >
            <CardContent
              sx={{
                p: '0px !important',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                    fontWeight: 600
                  }}
                >
                  Zapier
                </Typography>
                <Typography
                  color='text.secondary'
                  sx={{
                    mt: '4px',
                    fontSize: { xs: '11px', sm: '12px', md: '13px' },
                    fontWeight: 400
                  }}
                >
                  Integrate your Zapier automations with Success.ai to simplify and automate your daily tasks effortlessly.
                </Typography>
              </Box>
              <Box sx={{ mt: '4px', width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}
                >
                  {
                    ((isWhiteLabelUser && !isExcludedIntegrationFeature) || isZapierAllowed())
                      ? (<FormControlLabel
                          control={
                            <Switch
                              sx={{
                                ml: '4px',
                                mr: '4px'
                              }}
                              checked={isZapierEnabled}
                              onChange={handleZapierToggle}
                              name='enable_disable_zapier'
                            />
                        }
                          label='Enable'
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontSize: { xs: '10px', sm: '11px', md: '12px' }
                            }
                          }}
                         />)
                      : (
                        <Button
                          sx={{
                            height: '34px',
                            minWidth: '120px',
                            borderRadius: '6px',
                            gap: '4px',
                            flexShrink: 0,
                            fontSize: { xs: '10px', sm: '11px', md: '12px' }
                          }}
                          variant='contained'
                          onClick={handleExplorePlans}
                        >
                          <Starwhite sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                          <Typography
                            sx={{
                              fontSize: { xs: '10px', sm: '11px', md: '12px' },
                              fontWeight: 600,
                              color: '#fff'
                            }}
                          >
                            Explore Plans
                          </Typography>
                        </Button>
                        )
                  }
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {isZapierEnabled && ((isWhiteLabelUser && !isExcludedIntegrationFeature) || isZapierAllowed()) &&
                      (
                        <Tooltip title='Copy Zapier Key' placement='top' arrow>
                          <IconButton
                            sx={{
                              p: '4px',
                              bgcolor: '#F2F4F6',
                              border: '1px solid #E4E4E5',
                              borderRadius: '6px'
                            }}
                            onClick={handleCopyZapierKey}
                          >
                            <ContentCopyIcon
                              fontSize='small'
                              sx={{ color: 'primary.main' }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    <Tooltip title='View Documentation' placement='top' arrow>
                      <IconButton
                        component={Link}
                        href='/integrations/zapier-instructions'
                        target='_blank'
                        rel='noreferrer'
                        sx={{ p: '4px' }}
                      >
                        <ArticleOutlinedIcon
                          sx={{ fontSize: { xs: '16px', md: '20px' }, color: 'black' }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Warmup Connector Card */}
        {/* <Grid item xs={12} sm={12} md={4} lg={4} xl={3} xxl={2.4}>
          <Card
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              border: '1px solid #D7D9E6',
              borderRadius: '8px',
              minHeight: '220px',
              height: '100%',
              maxWidth: '320px',
              minWidth: '200px',
              boxSizing: 'border-box',
            }}
          >
            <CardContent
              sx={{
                p: '0px !important',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Box
                  sx={{
                    width: '100%',
                    height: '150px',
                    borderRadius: '6px',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src="/assets/warmupConnectorFrame.jpg"
                    alt="Warmup Connector"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      loading: 'lazy',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                    fontWeight: 600,
                    mt: '16px',
                  }}
                >
                  Warmup Connector
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{
                    mt: '4px',
                    fontSize: { xs: '11px', sm: '12px', md: '13px' },
                    fontWeight: 400,
                  }}
                >
                  Boost your email deliverability to keep your messages out of the spam folder
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={{
                  mt: '4px', // Reduced margin-top from 8px to 4px
                  height: '34px',
                  minWidth: '120px',
                  borderRadius: '6px',
                  fontSize: { xs: '10px', sm: '11px', md: '12px' },
                }}
              >
                Run Warmup Connector
              </Button>
            </CardContent>
          </Card>
        </Grid> */}

        {/* Hubspot Card */}
        <Grid item xs={12} sm={6} md={4} lg={4} xl={3} xxl={2}>
          <Card
            variant='outlined'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              border: '1px solid #D7D9E6',
              borderRadius: '8px',
              minHeight: '160px',
              maxHeight: 'auto',
              height: '100%',
              maxWidth: { xs: '100%', md: '320px' },
              minWidth: '200px',
              boxSizing: 'border-box'
            }}
          >
            <CardContent
              sx={{
                p: '0px !important',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                    fontWeight: 600
                  }}
                >
                  Hubspot
                </Typography>
                <Typography
                  color='text.secondary'
                  sx={{
                    mt: '4px',
                    fontSize: { xs: '11px', sm: '12px', md: '13px' },
                    fontWeight: 400
                  }}
                >
                  Connect HubSpot CRM to Success.ai to streamline workflows and deepen customer relationships.
                </Typography>
              </Box>
              <Box sx={{ mt: '4px', width: '100%' }}>
                {((isWhiteLabelUser && !isExcludedIntegrationFeature) || isHubSpotIntegrationAllowed())
                  ? (
                    <Button
                      variant='contained'
                      sx={{
                        height: '34px',
                        minWidth: '120px',
                        borderRadius: '6px',
                        gap: '4px',
                        flexShrink: 0,
                        fontSize: { xs: '10px', sm: '11px', md: '12px' }
                      }}
                      href={!HubSpotIntegrationData?.data ? config.HUBSPOT_LINK : undefined}
                      target={!HubSpotIntegrationData?.data ? '_blank' : undefined}
                      onClick={HubSpotIntegrationData?.data ? handleNavigate : undefined}
                    >
                      {!HubSpotIntegrationData?.data ? 'Start Integration' : 'HubSpot Settings'}
                    </Button>
                    )
                  : (
                    <Button
                      sx={{
                        height: '34px',
                        minWidth: '120px',
                        borderRadius: '6px',
                        gap: '4px',
                        flexShrink: 0,
                        fontSize: { xs: '10px', sm: '11px', md: '12px' }
                      }}
                      variant='contained'
                      onClick={handleExplorePlans}
                    >
                      <Starwhite sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '10px', sm: '11px', md: '12px' },
                          fontWeight: 600,
                          color: '#fff'
                        }}
                      >
                        Explore Plans
                      </Typography>
                    </Button>
                    )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Chrome Extension Card */}
        <Grid item xs={12} sm={6} md={4} lg={4} xl={3} xxl={2}>
          <Card
            variant='outlined'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              border: '1px solid #D7D9E6',
              borderRadius: '8px',
              minHeight: '160px',
              maxHeight: 'auto',
              height: '100%',
              maxWidth: { xs: '100%', md: '320px' },
              minWidth: '200px',
              boxSizing: 'border-box'
            }}
          >
            <CardContent
              sx={{
                p: '0px !important',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                    fontWeight: 600
                  }}
                >
                  Chrome Extension
                </Typography>
                <Typography
                  color='text.secondary'
                  sx={{
                    mt: '4px',
                    fontSize: { xs: '11px', sm: '12px', md: '13px' },
                    fontWeight: 400
                  }}
                >
                  Access Success.ai tools directly from your browser to capture leads, manage contacts, and boost productivity.
                </Typography>
              </Box>
              <Box sx={{ mt: '4px', width: '100%' }}>
                <Button
                  variant='contained'
                  sx={{
                    height: '34px',
                    minWidth: '120px',
                    borderRadius: '6px',
                    gap: '4px',
                    flexShrink: '0',
                    fontSize: { xs: '10px', sm: '11px', md: '12px' }
                  }}
                  onClick={handleChromeExtensionDownload}
                >
                  <Starwhite sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                  <Typography
                    sx={{
                      fontSize: { xs: '10px', sm: '11px', md: '12px' },
                      fontWeight: 600,
                      color: '#fff'
                    }}
                  >
                    Download
                  </Typography>
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Webhook Card */}
        <Grid item xs={12} sm={6} md={4} lg={4} xl={3} xxl={2}>
          <Card
            variant='outlined'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              border: '1px solid #D7D9E6',
              borderRadius: '8px',
              minHeight: '160px',
              maxHeight: 'auto',
              height: '100%',
              maxWidth: { xs: '100%', md: '320px' },
              minWidth: '200px',
              boxSizing: 'border-box'
            }}
          >
            <CardContent
              sx={{
                p: '0px !important',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                    fontWeight: 600
                  }}
                >
                  Webhook
                </Typography>
                <Typography
                  color='text.secondary'
                  sx={{
                    mt: '4px',
                    fontSize: { xs: '11px', sm: '12px', md: '13px' },
                    fontWeight: 400
                  }}
                >
                  Notify external services in real time when an event occurs in your Success.ai workspace.
                </Typography>
              </Box>
              <Box sx={{ mt: '4px', width: '100%' }}>
                {
                  ((isWhiteLabelUser && !isExcludedIntegrationFeature) || isWebhookAllowed())
                    ? (
                      <Button
                        variant='contained'
                        sx={{
                          height: '34px',
                          minWidth: '120px',
                          borderRadius: '6px',
                          gap: '4px',
                          flexShrink: '0',
                          fontSize: { xs: '10px', sm: '11px', md: '12px' }
                        }}
                        onClick={handleWebhookNavigate}
                      >
                        <Starwhite sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        <Typography
                          sx={{
                            fontSize: { xs: '10px', sm: '11px', md: '12px' },
                            fontWeight: 600,
                            color: '#fff'
                          }}
                        >
                          Go to Webhook
                        </Typography>
                      </Button>
                      )
                    : (
                      <Button
                        sx={{
                          height: '34px',
                          minWidth: '120px',
                          borderRadius: '6px',
                          gap: '4px',
                          flexShrink: 0,
                          fontSize: { xs: '10px', sm: '11px', md: '12px' }
                        }}
                        variant='contained'
                        onClick={handleExplorePlans}
                      >
                        <Starwhite sx={{ fontSize: { xs: '14px', md: '16px' } }} />
                        <Typography
                          sx={{
                            fontSize: { xs: '10px', sm: '11px', md: '12px' },
                            fontWeight: 600,
                            color: '#fff'
                          }}
                        >
                          Explore Plans
                        </Typography>
                      </Button>
                      )
                }
              </Box>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* SuccessAi API Generate Dialog */}
      <SuccessAiApiGenerateDialog
        open={openSuccessaiApiGenerateDialog}
        setOpenSuccessaiApiGenerateDialog={setOpenSuccessaiApiGenerateDialog}
        handleCloseSuccessaiApiDialog={handleCloseSuccessaiApiDialog}
        updateApiKey={updateApiKey}
        isLoadingUpdateApiKey={isLoadingUpdateApiKey}
        workspace={workspace}
      />

      <PremiumFeatureDialog open={openModal} onClose={handleCloseModal} />
    </Box>
  )
}

export default IntegrationsApiPage
